import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { SkillLevel } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save SkillLevel data.
 */
@Injectable()
export class SkillLevelService {
    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all SkillLevels for the specified event
     */
    public getIndex(
        eventId: string,
        divisionId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<SkillLevel[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId, null, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const skillLevels: SkillLevel[] = [];
                const bodyData: any = response.data || [];
                for (const skillLevel of bodyData) {
                    skillLevels.push((new SkillLevel(eventId)).deserialize(skillLevel));
                }
                return skillLevels;
            }));
    }

    /**
     * Get a single SkillLevel's data
     */
    public get(
        eventId: string,
        divisionId: string,
        skillLevelId: string,
        includes: string[] = null,
    ): Observable<SkillLevel> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId, skillLevelId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new SkillLevel().deserialize(response))));
    }

    /**
     * Add or update the SkillLevel
     */
    public save(skillLevel: SkillLevel, includes: string[] = null): Observable<SkillLevel> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string =
            this.getApiUrl(skillLevel.eventId, skillLevel.divisionId, skillLevel.id, includes);

        if (skillLevel.id) {
            // Update existing SkillLevel
            response = this.http.patch(apiRequestUrl, skillLevel.serialize());
        } else {
            // Add a new SkillLevel
            response = this.http.post(apiRequestUrl, skillLevel.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new SkillLevel()).deserialize(response)));
    }

    /**
     * Delete the specified SkillLevel.
     */
    public delete(skillLevel: SkillLevel): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(skillLevel.eventId, skillLevel.divisionId, skillLevel.id);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        divisionId: string,
        skillLevelId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string =
            `${this.http.configService.getApiBaseUrl()}/events/${eventId}/divisions/${divisionId}/skill-levels/`;

        if (skillLevelId) {
            url += skillLevelId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
